import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, Card, CardContent,
  Typography, Chip, Divider, IconButton, Tooltip, CircularProgress, Alert, ToggleButtonGroup, ToggleButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import * as eqpAgentApi from '../../lib/api/eqpAgent';
import { useCloseOnBack } from '../../hooks/useCloseOnBack';

// OS별 설치 안내 - 윈도우/리눅스 각각 설치 스크립트가 다르다
const INSTALL_STEPS = {
  windows: [
    '① 압축 풀기',
    '② install.bat 마우스 오른쪽 → 관리자 권한으로 실행',
    '③ KMES 주소(그대로 Enter)와 에이전트 토큰 붙여넣기',
    '④ "연결 확인 OK" 후 서비스 자동 등록 (재부팅해도 자동 실행, 비정상 종료 시 10초 뒤 재시작)',
  ],
  linux: [
    'tar xzf ….tar.gz && cd ksm-agent && sudo ./install.sh',
    '주소·토큰 입력',
    'systemd 서비스 자동 등록 확인: systemctl status ksm-agent',
  ],
};

// 전용 설치 파일(이 에이전트용)은 토큰 입력 없이 코드로 자동 등록된다 - 공용과 안내가 다르다
const PERSONAL_INSTALL_STEPS = {
  windows: [
    '① 압축 풀기',
    '② install.bat 마우스 오른쪽 → 관리자 권한으로 실행',
    '③ 주소·토큰을 묻지 않는다 (enroll.json에 24시간 등록 코드가 이미 들어있음)',
    '④ "연결 확인 OK" 후 서비스 자동 등록',
  ],
  linux: [
    'tar xzf ….tar.gz && cd ksm-agent && sudo ./install.sh',
    '주소·토큰을 묻지 않는다 (enroll.json에 24시간 등록 코드가 이미 들어있음)',
    'systemd 서비스 자동 등록 확인: systemctl status ksm-agent',
  ],
};

const OS_LABEL = { windows: '윈도우 (64비트)', linux: '리눅스 (64비트)' };

const formatSize = (bytes) => {
  if (bytes == null) return '-';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

const Sha256Field = ({ value }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      enqueueSnackbar('sha256을 복사했습니다.', { variant: 'success' });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      enqueueSnackbar('복사하지 못했습니다.', { variant: 'warning' });
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
      >
        sha256: {value}
      </Typography>
      <Tooltip title={copied ? '복사했습니다' : 'sha256 복사'}>
        <IconButton size="small" onClick={copy}>
          {copied ? <CheckIcon fontSize="inherit" color="success" /> : <ContentCopyIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/**
 * 설치 파일 받기 다이얼로그.
 *
 * agentUuid가 있으면 "공용 설치 파일"과 "이 에이전트용 설치 파일"(24시간·1회용 등록 코드 내장)을
 * 토글로 고를 수 있다. agentUuid가 없으면(목록 상단 버튼으로 열었을 때) 공용만 보여준다.
 */
const EqpAgentInstallerDialog = ({ open, onClose, agentUuid, agentNm }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  useCloseOnBack(open, onClose);
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingOs, setDownloadingOs] = useState(null);
  const [progress, setProgress] = useState(0);
  const [mode, setMode] = useState('generic'); // generic | personal
  const [enrollExpires, setEnrollExpires] = useState(null);

  useEffect(() => {
    if (!open) return;
    // 특정 에이전트에서 열었으면 입력 없이 설치되는 전용 파일이 기본 — 공용은 필요할 때만 고른다.
    setMode(agentUuid ? 'personal' : 'generic');
    setEnrollExpires(null);
    setLoading(true);
    eqpAgentApi.getAgentRelease()
      .then((res) => setRelease(eqpAgentApi.extractAgentRelease(res)))
      .catch(() => setRelease(null))
      .finally(() => setLoading(false));
  }, [open, agentUuid]);

  if (!open) return null;

  const handleDownload = async (os) => {
    setDownloadingOs(os);
    setProgress(0);
    try {
      const res = mode === 'personal'
        ? await eqpAgentApi.downloadAgentInstaller(agentUuid, os, setProgress)
        : await eqpAgentApi.downloadAgentRelease(os, setProgress);

      const fileInfo = (release?.files || []).find((f) => f.os === os);
      const fallbackName = fileInfo?.fileName || `ksm-agent-${os}-x64.zip`;
      const fileName = eqpAgentApi.extractFileNameFromResponse(res, fallbackName);

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);

      if (mode === 'personal') {
        const expires = eqpAgentApi.extractEnrollExpires(res);
        setEnrollExpires(expires);
      }
      enqueueSnackbar('설치 파일을 내려받았습니다.', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('설치 파일 다운로드에 실패했습니다.', { variant: 'error' });
    } finally {
      setDownloadingOs(null);
      setProgress(0);
    }
  };

  const steps = mode === 'personal' ? PERSONAL_INSTALL_STEPS : INSTALL_STEPS;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {agentNm ? `${agentNm} - 설치 파일 받기` : '설치 파일 받기'}
        </Box>
        <IconButton aria-label="닫기" onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : !release ? (
          <Typography variant="body2" color="text.secondary">아직 등록된 설치 파일이 없습니다.</Typography>
        ) : (
          <Box>
            {agentUuid && (
              <ToggleButtonGroup
                value={mode}
                exclusive
                size="small"
                onChange={(e, v) => v && setMode(v)}
                sx={{ mb: 2, flexWrap: 'wrap' }}
              >
                <ToggleButton value="personal">이 에이전트용 설치 파일</ToggleButton>
                <ToggleButton value="generic">공용 설치 파일</ToggleButton>
              </ToggleButtonGroup>
            )}

            {mode === 'personal' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                설치 파일에 24시간·1회용 등록 코드가 들어 있어 현장에서 install만 실행하면 됩니다.
                설치하면 이 에이전트의 토큰이 새로 바뀝니다.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap', mb: 2 }}>
              <Chip size="small" label={`v${release.version}`} color="primary" />
              {release.builtAt && (
                <Typography variant="caption" color="text.secondary">
                  빌드일시 {dayjs(release.builtAt).format('YYYY-MM-DD HH:mm')}
                </Typography>
              )}
            </Box>

            {enrollExpires && (
              <Alert severity="success" sx={{ mb: 2 }}>
                등록 코드 만료: {dayjs(enrollExpires).format('YYYY-MM-DD HH:mm')}까지
              </Alert>
            )}

            <Grid container spacing={2}>
              {['windows', 'linux'].map((os) => {
                const fileInfo = (release.files || []).find((f) => f.os === os);
                const downloading = downloadingOs === os;
                return (
                  <Grid item xs={12} sm={6} key={os}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                          {OS_LABEL[os]}
                        </Typography>
                        {fileInfo ? (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {formatSize(fileInfo.size)}
                            </Typography>
                            {/* 전용 파일은 등록 코드를 넣어 다시 묶으므로 해시가 공용 파일과 달라진다 — 공용일 때만 보여 준다 */}
                            {mode === 'generic' && <Sha256Field value={fileInfo.sha256} />}
                            <Button
                              fullWidth
                              variant="contained"
                              sx={{ mt: 1.5 }}
                              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                              disabled={downloading}
                              onClick={() => handleDownload(os)}
                            >
                              {downloading ? `받는 중... ${progress}%` : '다운로드'}
                            </Button>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            이 운영체제용 설치 파일이 없습니다.
                          </Typography>
                        )}

                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                          설치 순서
                        </Typography>
                        {steps[os].map((step, i) => (
                          <Typography key={i} variant="caption" color="text.secondary" component="div">
                            {step}
                          </Typography>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EqpAgentInstallerDialog;
